import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateSuggestion } from '@/application/services/suggestion.service'
import { Button } from '@/ui/components/common/Button'
import { Input } from '@/ui/components/common/Input'
import { Label } from '@/ui/components/common/Label'
import { Textarea } from '@/ui/components/common/Textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/ui/components/common/Dialog'

/**
 * Modal form letting an authenticated user submit a new investigation suggestion
 * (title + optional description). Resets local state and closes on success.
 */
export function SuggestDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const { mutate: createSuggestion, isPending } = useCreateSuggestion()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!title.trim()) return
    createSuggestion(
      { title, description: description || undefined },
      {
        onSuccess: () => {
          setTitle('')
          setDescription('')
          onOpenChange(false)
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('investigations.suggest_new')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="suggest-title">{t('investigations.suggestion_title_label')}</Label>
              <Input
                id="suggest-title"
                className="mt-1"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="suggest-desc">
                {t('investigations.suggestion_description_label')}
              </Label>
              <Textarea
                id="suggest-desc"
                className="mt-1"
                rows={3}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="submit" isLoading={isPending}>
                {t('investigations.suggestion_submit')}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
